import React, { createContext, useState, useEffect, useContext } from 'react';
import { translations as baseTranslations } from '../i18n/i18n';
import axios from 'axios';

export const LanguageContext = createContext();

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Detect language based on user's country
const detectLanguageFromLocation = async (registeredLangs) => {
  try {
    const response = await fetch('https://ipapi.co/json/', { 
      timeout: 3000 
    });
    const data = await response.json();
    const countryCode = data.country_code;
    
    // Map countries to language codes
    const countryToLang = {
      'JP': 'ja', 'KR': 'ko', 'KP': 'ko',
      'CN': 'zh', 'TW': 'zh', 'HK': 'zh',
      'RU': 'ru', 'IT': 'it', 'PT': 'pt', 'BR': 'pt',
      'ES': 'es', 'MX': 'es', 'AR': 'es',
      'FR': 'fr', 'TH': 'th', 'VN': 'vi'
    };
    
    const detectedLang = countryToLang[countryCode] || 'en';
    
    // Only return detected language if it's registered
    if (registeredLangs.includes(detectedLang)) {
      return detectedLang;
    }
    return 'en';
  } catch (err) {
    return 'en';
  }
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguageState] = useState(() => {
    const saved = localStorage.getItem('lang');
    return saved || 'en';
  });
  const [isDetecting, setIsDetecting] = useState(true);
  const [registeredLanguages, setRegisteredLanguages] = useState(['en', 'ko', 'ja']); // Default core languages
  // Runtime i18n merged with bundled English source — includes any languages added post-deploy
  const [translations, setTranslations] = useState(baseTranslations);

  // Fetch registered languages + runtime i18n from backend
  useEffect(() => {
    const fetchRegisteredLanguages = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/languages`);
        if (response.data.languages) {
          const langCodes = response.data.languages.map(l => l.code);
          setRegisteredLanguages(langCodes);
          return langCodes;
        }
      } catch (err) {
        // Fallback to core languages if API fails
      }
      return ['en', 'ko', 'ja'];
    };

    const fetchI18n = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/i18n`);
        if (response.data && typeof response.data === 'object') {
          // Overlay onto bundled English so new post-deploy languages work
          setTranslations({ ...baseTranslations, ...response.data });
        }
      } catch (err) {
        // Keep bundled translations as fallback
      }
    };

    const initLanguage = async () => {
      const [registeredLangs] = await Promise.all([fetchRegisteredLanguages(), fetchI18n()]);
      const hasVisited = localStorage.getItem('hasVisited');
      
      if (!hasVisited) {
        const detectedLang = await detectLanguageFromLocation(registeredLangs);
        setLanguageState(detectedLang);
        localStorage.setItem('lang', detectedLang);
        localStorage.setItem('hasVisited', 'true');
      } else {
        // Validate saved language is still registered
        const savedLang = localStorage.getItem('lang');
        if (savedLang && !registeredLangs.includes(savedLang)) {
          setLanguageState('en');
          localStorage.setItem('lang', 'en');
        }
      }
      setIsDetecting(false);
    };
    
    initLanguage();
  }, []);

  useEffect(() => {
    localStorage.setItem('lang', language);
    document.documentElement.lang = language;
  }, [language]);

  const setLanguage = (lang) => {
    // Only allow switching to registered languages
    if (registeredLanguages.includes(lang)) {
      setLanguageState(lang);
    }
  };

  const t = (path) => {
    const keys = path.split('.');
    let value = translations[language];
    
    // If translation not found in current language, fallback to English
    if (!value) {
      value = translations['en'];
    }
    
    for (const key of keys) {
      if (value && typeof value === 'object') {
        value = value[key];
      } else {
        // Fallback to English for this specific path
        let englishValue = translations['en'];
        for (const k of keys) {
          if (englishValue && typeof englishValue === 'object') {
            englishValue = englishValue[k];
          } else {
            return path; // Final fallback to key
          }
        }
        return englishValue || path;
      }
    }
    
    return value || path;
  };

  return (
    <LanguageContext.Provider value={{ 
      language, 
      setLanguage, 
      t, 
      translations, 
      isDetecting,
      registeredLanguages 
    }}>
      {children}
    </LanguageContext.Provider>
  );
};

// Hook for accessing language context
export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return context;
};
